import { isNilOrEmpty } from './objectUtils';

export const EMAIL_CHANGE_CALLBACK_PARAM = 'emailChange';
export const EMAIL_CHANGE_CALLBACK_ERROR_PARAM = 'error';
const VERIFICATION_CALLBACK_URL_PARAM = 'callbackURL';

export enum EmailChangeCallbackStep {
  CONFIRMED = 'confirmed',
  VERIFIED = 'verified',
}

export interface EmailChangeCallbackResult {
  step: EmailChangeCallbackStep;
  errorCode?: string;
}

const toEmailChangeCallbackStep = (value: string | null): EmailChangeCallbackStep | undefined => {
  switch (value) {
    case EmailChangeCallbackStep.CONFIRMED:
      return EmailChangeCallbackStep.CONFIRMED;
    case EmailChangeCallbackStep.VERIFIED:
      return EmailChangeCallbackStep.VERIFIED;
    default:
      return undefined;
  }
};

/** URL the auth server sends the browser back to once the current address has approved the change. */
export const buildEmailChangeCallbackUrl = (origin: string, path: string): string =>
  `${origin}${path}?${EMAIL_CHANGE_CALLBACK_PARAM}=${EmailChangeCallbackStep.CONFIRMED}`;

/**
 * The auth server reuses the callback of the first link for the second one: this rewrites it inside the
 * verification link sent to the new address, so the app can tell the two returns apart.
 */
export const markEmailChangeCallbackVerified = (verificationUrl: string): string => {
  try {
    const verification = new URL(verificationUrl);
    const callback = new URL(verification.searchParams.get(VERIFICATION_CALLBACK_URL_PARAM) ?? '');
    if (!callback.searchParams.has(EMAIL_CHANGE_CALLBACK_PARAM)) {
      return verificationUrl;
    }
    callback.searchParams.set(EMAIL_CHANGE_CALLBACK_PARAM, EmailChangeCallbackStep.VERIFIED);
    verification.searchParams.set(VERIFICATION_CALLBACK_URL_PARAM, callback.toString());
    return verification.toString();
  } catch {
    return verificationUrl;
  }
};

export const parseEmailChangeCallback = (search: string): EmailChangeCallbackResult | undefined => {
  const params = new URLSearchParams(search);
  const step = toEmailChangeCallbackStep(params.get(EMAIL_CHANGE_CALLBACK_PARAM));
  if (step === undefined) {
    return undefined;
  }
  const errorCode = params.get(EMAIL_CHANGE_CALLBACK_ERROR_PARAM);
  return isNilOrEmpty(errorCode) ? { step } : { step, errorCode };
};
